import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2,
  MessageCircle,
  AlertCircle,
  Calendar,
  Clock,
  Send,
  ArrowLeft,
  Sparkles,
  ExternalLink,
  Phone,
} from "lucide-react";
import {
  getChatContext,
  sendFlowAction,
  getChatGreeting,
  sendChatMessage,
  submitChatLead,
  type WidgetMode,
  type ChatWidgetContext,
  type ChatMessage,
  type FlowState,
  type AiCitation,
} from "@/lib/chat-api";

// ════════════════════════════════════════════════════════════════════════════
// Chat Page Component — AI Front Desk + Flow-Based Booking (iframe embed)
// ════════════════════════════════════════════════════════════════════════════
// Two modes:
//   • "chat"   — free-text input → POST /api/chatbot/:slug/message
//   • "wizard" — button-driven booking flow → POST /api/chatbot/:slug/flow

const SESSION_STORAGE_KEY = "appflo_chat_session";

function readOrCreateSessionId(): string {
  if (typeof window === "undefined") return crypto.randomUUID();
  try {
    const existing = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    window.localStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
}

export function ChatPage() {
  const { slug } = useParams<{ slug: string }>();

  const [context, setContext] = useState<ChatWidgetContext | null>(null);
  const [contextLoading, setContextLoading] = useState(true);

  const [mode, setMode] = useState<WidgetMode>("chat");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [flowState, setFlowState] = useState<FlowState | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});

  // Chat-mode state
  const sessionIdRef = useRef<string>(readOrCreateSessionId());
  const [chatInput, setChatInput] = useState("");
  const [greetingLoaded, setGreetingLoaded] = useState(false);

  // Inline lead capture
  const [leadFormVisible, setLeadFormVisible] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: "",
    phone: "",
    email: "",
    question: "",
  });
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ── Context bootstrap ──────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function loadContext() {
      if (!slug) return;
      try {
        setContextLoading(true);
        const ctx = await getChatContext(slug);
        if (!cancelled) setContext(ctx);
      } catch (err) {
        console.error("Failed to load chat context:", err);
        if (!cancelled) setError("Chat is currently unavailable. Please try again later.");
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    }
    loadContext();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // ── Greeting bootstrap ─────────────────────────────────────────────────
  useEffect(() => {
    if (!context || !slug || greetingLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const { greeting } = await getChatGreeting(slug);
        if (cancelled) return;
        setMessages((prev) => {
          if (prev.length > 0) return prev;
          return [
            {
              id: "greeting",
              role: "assistant",
              content: greeting,
              timestamp: new Date(),
            },
          ];
        });
      } catch (err) {
        console.error("Failed to load greeting:", err);
      } finally {
        if (!cancelled) setGreetingLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [context, slug, greetingLoaded]);

  // ── Auto-scroll on new messages ────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, leadFormVisible]);

  // ── Wizard mode lazy-init ──────────────────────────────────────────────
  const ensureWizardStarted = useCallback(async () => {
    if (!context || !slug || flowState) return;
    setLoading(true);
    setError(null);
    try {
      const response = await sendFlowAction(slug, "start");
      setFlowState(response.state);
      const servicesMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: response.message,
        timestamp: new Date(),
        options: response.options,
        optionType: response.optionType,
      };
      setMessages((prev) => [...prev, servicesMsg]);
    } catch (err) {
      console.error("Failed to start flow:", err);
      setError("Failed to load services. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [context, flowState, slug]);

  const switchToWizard = useCallback(async () => {
    setMode("wizard");
    await ensureWizardStarted();
  }, [ensureWizardStarted]);

  const switchToChat = useCallback(() => {
    setMode("chat");
  }, []);

  // ── Wizard-mode handlers ───────────────────────────────────────────────
  const handleOptionClick = async (optionId: string, optionLabel: string) => {
    if (loading || !flowState || !slug) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: optionLabel,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);

    setLoading(true);
    setError(null);

    try {
      let action: string;
      let data: Record<string, unknown> = {};

      switch (flowState.step) {
        case "services":
          action = "select_service";
          data = { serviceId: optionId };
          break;
        case "providers":
          action = "select_provider";
          data = { providerId: optionId };
          break;
        case "dates":
          action = "select_date";
          data = { date: optionId };
          break;
        case "times":
          action = "select_time";
          data = { time: optionId };
          break;
        case "confirm":
          if (optionId === "confirm") {
            action = "confirm_booking";
          } else {
            action = "restart";
          }
          break;
        case "complete":
          action = "restart";
          break;
        default:
          action = "start";
      }

      const response = await sendFlowAction(slug, action, flowState, data);
      setFlowState(response.state);

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: response.message,
        timestamp: new Date(),
        options: response.options,
        optionType: response.optionType,
        formFields: response.formFields,
      };

      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      console.error("Flow error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !flowState || !slug) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: `Name: ${formData.name}\nEmail: ${formData.email}${formData.phone ? `\nPhone: ${formData.phone}` : ""}`,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);

    setLoading(true);
    setError(null);

    try {
      const response = await sendFlowAction(slug, "submit_contact", flowState, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      });
      setFlowState(response.state);
      setFormData({});

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: response.message,
        timestamp: new Date(),
        options: response.options,
        optionType: response.optionType,
      };

      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      console.error("Form submit error:", err);
      setError("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Chat-mode handlers ─────────────────────────────────────────────────
  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug) return;
    const trimmed = chatInput.trim();
    if (!trimmed || loading) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: trimmed,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setChatInput("");
    setLoading(true);
    setError(null);

    try {
      const response = await sendChatMessage(slug, {
        sessionId: sessionIdRef.current,
        message: trimmed,
      });

      if (response.sessionId) {
        sessionIdRef.current = response.sessionId;
        try {
          window.localStorage.setItem(SESSION_STORAGE_KEY, response.sessionId);
        } catch {
          /* ignore */
        }
      }

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: response.response,
        timestamp: new Date(),
        citations: response.citations,
        toolCalls: response.toolCalls,
      };
      setMessages((prev) => [...prev, assistantMsg]);

      // Quota fall-back: when the account hits its monthly cap the server
      // returns the graceful hand-off response with `quotaExhausted: true`.
      // Switch the widget to wizard mode so the visitor still has a working
      // path to book an appointment (PRD §11 fail-safe).
      if (response.quotaExhausted) {
        await switchToWizard();
        return;
      }

      const tools = response.toolCalls || [];
      const escalate = tools.find((t) => t.name === "escalate_to_human");
      const book = tools.find((t) => t.name === "book_appointment");

      if (escalate) {
        setLeadForm((prev) => ({
          ...prev,
          question: prev.question || trimmed,
        }));
        setLeadFormVisible(true);
      }
      // When Claude calls book_appointment, DON'T auto-switch to wizard
      // (it's jarring and fails if no services are configured). Instead,
      // the persistent CTA bar below the input handles the conversion.
      // The tool call is still logged for analytics.
      void book;
    } catch (err) {
      const e = err as Error & { status?: number; retryAfterMs?: number };
      console.error("Chat error:", e);
      if (e.status === 429 && e.retryAfterMs) {
        setError(
          `Slow down a moment — you can send another message in ${Math.ceil(e.retryAfterMs / 1000)}s.`
        );
      } else {
        setError(e.message || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !slug) return;
    if (!leadForm.name.trim() || (!leadForm.phone.trim() && !leadForm.email.trim())) {
      setError("Please enter your name and either a phone or email.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await submitChatLead(slug, {
        sessionId: sessionIdRef.current,
        name: leadForm.name.trim(),
        phone: leadForm.phone.trim() || undefined,
        email: leadForm.email.trim() || undefined,
        question: leadForm.question.trim() || undefined,
      });
      setLeadSubmitted(true);
      setLeadFormVisible(false);

      const confirmationMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content:
          "Thanks! Someone from our team will reach out shortly. In the meantime, feel free to keep chatting or book an appointment using the button up top.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, confirmationMsg]);
    } catch (err) {
      console.error("Lead submit error:", err);
      setError(err instanceof Error ? err.message : "Failed to submit lead.");
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (contextLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading chat...</p>
        </div>
      </div>
    );
  }

  if (!context) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            Chat Unavailable
          </h2>
          <p className="text-sm text-gray-500">
            {error || "Unable to load chat. Please check the URL and try again."}
          </p>
        </div>
      </div>
    );
  }

  const { branding } = context;
  const primaryColor = branding.primaryColor || "#7c3aed";

  return (
    <div className="flex flex-col bg-white h-screen">
      {/* Header */}
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 text-white shrink-0"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="flex items-center gap-3 min-w-0">
          {context.logoUrl ? (
            <img
              src={context.logoUrl}
              alt={context.businessName}
              className="h-8 w-auto shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="font-semibold truncate">{context.businessName}</h1>
            <p className="text-sm opacity-80 truncate">
              {mode === "chat" ? "Front Desk AI" : "Book an Appointment"}
            </p>
          </div>
        </div>
        {mode === "chat" ? (
          <button
            onClick={switchToWizard}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors text-xs font-medium shrink-0"
            aria-label="Set an appointment"
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Appointment</span>
          </button>
        ) : (
          <button
            onClick={switchToChat}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors text-xs font-medium shrink-0"
            aria-label="Back to chat"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Chat</span>
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((message) => (
          <div key={message.id}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                  message.role === "user"
                    ? "rounded-br-md text-white"
                    : "rounded-bl-md bg-white shadow-sm border border-gray-100 text-gray-800"
                }`}
                style={
                  message.role === "user"
                    ? { backgroundColor: primaryColor }
                    : undefined
                }
              >
                <MessageBody
                  content={message.content}
                  primaryColor={primaryColor}
                  isUser={message.role === "user"}
                />
                {message.role === "assistant" && message.citations && message.citations.length > 0 && (
                  <CitationList
                    citations={message.citations}
                    primaryColor={primaryColor}
                  />
                )}
              </div>
            </motion.div>

            {/* Inline disclaimer + "Talk to Doctor" button after every AI response (not the greeting) */}
            {message.role === "assistant" && message.id !== "greeting" && !message.options && (
              <div className="mt-2 flex items-start gap-2 max-w-[85%]">
                <div className="flex-1 text-[11px] text-gray-400 leading-relaxed">
                  I'm an AI assistant, not a medical professional. For personalized advice, speak directly with the doctor.
                </div>
                <button
                  onClick={() => {
                    setLeadForm((prev) => ({ ...prev, question: (messages.slice(0, messages.indexOf(message)).reverse().find(m => m.role === "user")?.content || "").slice(0, 200) }));
                    setLeadFormVisible(true);
                  }}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-white text-[11px] font-medium shrink-0 whitespace-nowrap"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Phone className="w-3 h-3" />
                  Talk to Doctor
                </button>
              </div>
            )}

            {message.role === "assistant" && message.options && message.options.length > 0 && (
              <div className="mt-3 space-y-2">
                {message.optionType === "date" || message.optionType === "time" ? (
                  <div className="flex flex-wrap gap-2">
                    {message.options.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleOptionClick(option.id, option.label)}
                        disabled={loading}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all hover:scale-[1.02] disabled:opacity-50"
                        style={{
                          borderColor: primaryColor,
                          color: primaryColor,
                        }}
                      >
                        {message.optionType === "date" ? (
                          <Calendar className="w-3.5 h-3.5" />
                        ) : (
                          <Clock className="w-3.5 h-3.5" />
                        )}
                        <span>{option.label}</span>
                        {option.sublabel && (
                          <span className="text-xs opacity-70">({option.sublabel})</span>
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {message.options.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleOptionClick(option.id, option.label)}
                        disabled={loading}
                        className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium border-2 transition-all hover:scale-[1.01] disabled:opacity-50"
                        style={{
                          borderColor: primaryColor,
                          backgroundColor: `${primaryColor}08`,
                        }}
                      >
                        <span style={{ color: primaryColor }}>{option.label}</span>
                        {option.sublabel && (
                          <span className="block text-xs text-gray-500 mt-0.5">
                            {option.sublabel}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {message.role === "assistant" && message.optionType === "form" && message.formFields && (
              <form onSubmit={handleFormSubmit} className="mt-3 space-y-3">
                {message.formFields.map((field) => (
                  <div key={field.name}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type={field.type}
                      required={field.required}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          [field.name]: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      style={
                        {
                          "--tw-ring-color": primaryColor,
                        } as React.CSSProperties
                      }
                    />
                  </div>
                ))}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  {loading ? "Processing..." : "Continue"}
                </button>
              </form>
            )}
          </div>
        ))}

        {leadFormVisible && !leadSubmitted && (
          <LeadForm
            primaryColor={primaryColor}
            values={leadForm}
            onChange={(patch) => setLeadForm((prev) => ({ ...prev, ...patch }))}
            onSubmit={handleLeadSubmit}
            loading={loading}
            onCancel={() => setLeadFormVisible(false)}
          />
        )}

        {loading && messages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="bg-white shadow-sm border border-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
              <div className="flex items-center gap-2">
                <Loader2
                  className="w-4 h-4 animate-spin"
                  style={{ color: primaryColor }}
                />
                <span className="text-sm text-gray-500">
                  {mode === "chat" ? "Thinking..." : "Finding options..."}
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {error && (
          <div className="flex justify-center">
            <p className="text-xs text-red-500 bg-red-50 px-3 py-1.5 rounded-full">
              {error}
            </p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* CTA bar — always visible in chat mode once any AI response exists */}
      {mode === "chat" && messages.length > 1 && !leadFormVisible && (
        <div className="px-3 pt-2 pb-1 bg-white border-t border-gray-100 shrink-0">
          <div
            className="flex items-center gap-2 p-2.5 rounded-xl text-xs"
            style={{ backgroundColor: `${primaryColor}08`, borderColor: `${primaryColor}20`, borderWidth: 1 }}
          >
            <span className="flex-1 text-gray-600">
              Want personalized advice from the doctor?
            </span>
            <button
              onClick={() => {
                setLeadForm((prev) => ({ ...prev, question: (messages.slice().reverse().find(m => m.role === "user")?.content || "").slice(0, 200) }));
                setLeadFormVisible(true);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-medium shrink-0"
              style={{ backgroundColor: primaryColor }}
            >
              <Phone className="w-3 h-3" />
              Have Us Call You
            </button>
            <button
              onClick={switchToWizard}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium shrink-0"
              style={{ borderColor: primaryColor, color: primaryColor }}
            >
              <Calendar className="w-3 h-3" />
              Book
            </button>
          </div>
        </div>
      )}

      {/* Footer — chat input bar (chat mode) or back-to-chat link (wizard mode) */}
      {mode === "chat" ? (
        <form
          onSubmit={handleChatSubmit}
          className="p-3 border-t border-gray-100 bg-white flex items-center gap-2 shrink-0"
        >
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder="Ask about services, hours, or book a visit..."
            disabled={loading}
            maxLength={2000}
            className="flex-1 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 disabled:opacity-50"
            style={
              {
                "--tw-ring-color": primaryColor,
              } as React.CSSProperties
            }
          />
          <button
            type="submit"
            disabled={loading || !chatInput.trim()}
            className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-opacity disabled:opacity-40"
            style={{ backgroundColor: primaryColor }}
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      ) : (
        <div className="p-3 border-t border-gray-100 bg-white text-center shrink-0">
          <button
            onClick={switchToChat}
            className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
            style={{ color: primaryColor }}
          >
            <ArrowLeft className="w-3 h-3" />
            Back to chat
          </button>
        </div>
      )}

      <div className="px-3 pb-2 bg-white shrink-0">
        <p className="text-center text-[10px] text-gray-400">
          Powered by{" "}
          <a
            href="https://appointmentflo.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
            style={{ color: primaryColor }}
          >
            AppointmentFlo.com
          </a>
        </p>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Helpers (mirrored from ChatWidget — kept local to avoid a new shared file
// for two small components; revisit if a third surface is added.)
// ════════════════════════════════════════════════════════════════════════════

function MessageBody({
  content,
  primaryColor,
  isUser,
}: {
  content: string;
  primaryColor: string;
  isUser: boolean;
}) {
  const linkColor = isUser ? "rgba(255,255,255,0.95)" : primaryColor;
  const parts: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }
    parts.push(
      <a
        key={`lnk-${key++}`}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="underline"
        style={{ color: linkColor }}
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }
  return <p className="text-sm whitespace-pre-wrap leading-relaxed">{parts}</p>;
}

function CitationList({
  citations,
  primaryColor,
}: {
  citations: AiCitation[];
  primaryColor: string;
}) {
  if (citations.length === 0) return null;
  return (
    <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap gap-1.5">
      {citations.map((c, i) => (
        <a
          key={`${c.url}-${i}`}
          href={c.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border hover:bg-gray-50"
          style={{ borderColor: `${primaryColor}40`, color: primaryColor }}
        >
          <ExternalLink className="w-2.5 h-2.5" />
          <span className="truncate max-w-[140px]">{c.title || c.url}</span>
        </a>
      ))}
    </div>
  );
}

function LeadForm({
  primaryColor,
  values,
  onChange,
  onSubmit,
  loading,
  onCancel,
}: {
  primaryColor: string;
  values: { name: string; phone: string; email: string; question: string };
  onChange: (patch: Partial<{ name: string; phone: string; email: string; question: string }>) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  onCancel: () => void;
}) {
  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border rounded-2xl shadow-sm p-4 space-y-3"
      style={{ borderColor: `${primaryColor}30` }}
    >
      <div>
        <h4 className="text-sm font-semibold" style={{ color: primaryColor }}>
          Connect with our team
        </h4>
        <p className="text-xs text-gray-500 mt-0.5">
          Leave your details and someone will reach out shortly.
        </p>
      </div>
      <input
        type="text"
        required
        placeholder="Full name"
        value={values.name}
        onChange={(e) => onChange({ name: e.target.value })}
        className="w-full px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
        style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
      />
      <input
        type="tel"
        placeholder="Phone"
        value={values.phone}
        onChange={(e) => onChange({ phone: e.target.value })}
        className="w-full px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
        style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
      />
      <input
        type="email"
        placeholder="Email"
        value={values.email}
        onChange={(e) => onChange({ email: e.target.value })}
        className="w-full px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
        style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
      />
      <textarea
        rows={2}
        placeholder="What can we help with?"
        value={values.question}
        onChange={(e) => onChange({ question: e.target.value })}
        className="w-full px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 resize-none"
        style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          Not now
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: primaryColor }}
        >
          {loading ? "Sending..." : "Send"}
        </button>
      </div>
    </form>
  );
}

export default ChatPage;
